import React, { type ReactNode, useContext, useMemo } from 'react';

import { Flex } from '@banhala/ui-kit';
import { ScrollContext } from '../../context/ScrollContext';
import { ScrollToBottomButton, ScrollToTopButton } from 'shared/src/component';
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { useGoodsPromotionComponents } from '../../queries/useGoodsPromotionComponents';
import { PromotionFloatingButton } from './components/PromotionFloatingButton';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';
import { FloatingButtonsContainer } from './components/FloatingButtonsContainer';
import { isAndroid } from 'shared/src/utils';
import { useScrollThresholdValue } from '../../hooks/useScrollThresholdValue';

const FIXED_OFFSET = 16;
const BUTTON_SIZE = 40;

type FloatingButtonsProps = {
  bottomOffset: number;
  scrollToYPosition: number;
  // stickyItemPositionY: number;
};

const ButtonContainer: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Flex
    shadowStep={'medium'}
    width={BUTTON_SIZE}
    height={BUTTON_SIZE}
    borderRadius={BUTTON_SIZE * 0.5}
  >
    {children}
  </Flex>
);

const FloatingButtonsAnimatedView: React.FC<
  FloatingButtonsProps & { children?: ReactNode }
> = props => {
  const { listRef } = useContext(ScrollContext);
  const visibilityValue = useScrollThresholdValue(
    isAndroid
      ? props.scrollToYPosition + SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
      : SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
    isAndroid ? SCROLL_ANIMATION_DURATION * 0.5 : SCROLL_ANIMATION_DURATION,
  );

  const style = useAnimatedStyle(() => ({
    position: 'absolute',
    bottom: props.bottomOffset + FIXED_OFFSET + 32,
    right: FIXED_OFFSET,
    alignItems: 'center',
    gap: 12,
    opacity: isAndroid
      ? visibilityValue.value > 0.5
        ? 1
        : 0
      : visibilityValue.value,
  }));

  const animatedProps = useAnimatedProps(() => ({
    pointerEvents:
      visibilityValue.value > 0.5 ? ('auto' as const) : ('none' as const),
  }));

  return (
    <Animated.View style={style} animatedProps={animatedProps}>
      <ButtonContainer>
        <ScrollToTopButton
          testID={'탑버튼'}
          onPress={() => {
            listRef?.current?.scrollToOffset({
              offset: props.scrollToYPosition,
              animated: true,
            });
          }}
        />
      </ButtonContainer>
      <ButtonContainer>
        <ScrollToBottomButton
          testID={'바텀버튼'}
          onPress={() => {
            listRef?.current?.scrollToEnd({ animated: true });
          }}
        />
      </ButtonContainer>
      {props.children}
    </Animated.View>
  );
};

const FloatingButtonContent: React.FC<FloatingButtonsProps> = props => {
  const { data: promotionComponents } =
    useGoodsPromotionComponents('GOODS_DETAIL');

  const floatingButton = useMemo(() => {
    const buttons = promotionComponents.component?.floating_buttons ?? [];
    if (buttons.length === 0) return null;
    return {
      button: buttons[Math.floor(Math.random() * buttons.length)],
      promotionSno: promotionComponents.component?.promotion_sno,
    };
  }, [
    promotionComponents.component?.floating_buttons,
    promotionComponents.component?.promotion_sno,
  ]);

  return (
    <FloatingButtonsAnimatedView {...props}>
      {floatingButton && (
        <PromotionFloatingButton
          button={floatingButton.button}
          buttonIdx={0}
          promotionSno={floatingButton.promotionSno}
        />
      )}
    </FloatingButtonsAnimatedView>
  );
};

export const FloatingButtons: React.FC<FloatingButtonsProps> = props => {
  return (
    <FloatingButtonsContainer>
      <React.Suspense fallback={<FloatingButtonsAnimatedView {...props} />}>
        <FloatingButtonContent {...props} />
      </React.Suspense>
    </FloatingButtonsContainer>
  );
};
